/**
 * Pure day-filtering + geometry helpers.
 *
 * All functions here are side-effect free and independent of MapLibre so they
 * can be unit-tested in isolation. The map layer consumes their output to draw
 * markers and to `fitBounds`.
 */

import {
  ALL_DAYS,
  type Category,
  type Day,
  type DaySelection,
  type Hotel,
  type Itinerary,
  type Stop,
} from './types';

/** A simple geographic bounding box (degrees). */
export interface Bounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** Anything with a coordinate. */
export interface LatLng {
  lat: number;
  lng: number;
}

/** Find a day by its 1-based number. */
export function findDay(itinerary: Itinerary, n: number): Day | undefined {
  return itinerary.days.find((d) => d.n === n);
}

/**
 * The stops to show for a selection: a single day's stops, or every stop
 * across all days for {@link ALL_DAYS}.
 */
export function stopsForSelection(itinerary: Itinerary, selection: DaySelection): Stop[] {
  if (selection === ALL_DAYS) return itinerary.days.flatMap((d) => d.stops);
  return findDay(itinerary, selection)?.stops ?? [];
}

const DASH = /[–—-]/; // en-dash, em-dash, hyphen

interface DayMonth {
  day: number;
  month?: number;
}

function parseDayMonth(token: string): DayMonth | null {
  const m = token.trim().match(/^(\d{1,2})(?:\/(\d{1,2}))?$/);
  if (!m) return null;
  const day = Number(m[1]);
  const month = m[2] !== undefined ? Number(m[2]) : undefined;
  if (day < 1 || day > 31) return null;
  if (month !== undefined && (month < 1 || month > 12)) return null;
  return { day, month };
}

/**
 * Parse a hotel "nights" string into a concrete date range for the trip year.
 * Handles same-month ranges ("23–27/7"), cross-month ranges ("27/7–1/8") and
 * single dates ("1/8"). Returns `null` if it cannot be parsed.
 */
export function parseNightRange(nights: string, year: number): { start: Date; end: Date } | null {
  const parts = nights.split(DASH);
  if (parts.length === 1) {
    const only = parseDayMonth(parts[0]!);
    if (!only || only.month === undefined) return null;
    const d = new Date(year, only.month - 1, only.day);
    return { start: d, end: new Date(d.getTime()) };
  }
  if (parts.length !== 2) return null;

  const left = parseDayMonth(parts[0]!);
  const right = parseDayMonth(parts[1]!);
  if (!left || !right) return null;

  // A missing month inherits from the other side (e.g. "23–27/7").
  const startMonth = left.month ?? right.month;
  const endMonth = right.month ?? left.month;
  if (startMonth === undefined || endMonth === undefined) return null;

  let start = new Date(year, startMonth - 1, left.day);
  const end = new Date(year, endMonth - 1, right.day);
  // If the left day had no month and inheriting the right side's month makes the
  // range run backwards (e.g. "30–2/8" = 30 Jul → 2 Aug), the left day belongs
  // to the previous month.
  if (left.month === undefined && start.getTime() > end.getTime()) {
    start = new Date(year, startMonth - 2, left.day);
  }
  return { start, end };
}

function parseISODateLocal(iso: string): Date | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/**
 * The hotels relevant to a selection.
 * For {@link ALL_DAYS} that's every hotel; for a specific day it's the hotel
 * you sleep at that night — so the base shows on the map even on days without a
 * hotel stop. The checkout date belongs to the *next* base, so for multi-night
 * stays the end date is treated as exclusive; single-date stays are inclusive.
 */
export function hotelsForSelection(
  itinerary: Itinerary,
  selection: DaySelection,
  year: number,
): Hotel[] {
  if (selection === ALL_DAYS) return itinerary.hotels;
  const day = findDay(itinerary, selection);
  if (!day) return [];
  const date = parseISODateLocal(day.date);
  if (!date) return [];
  const t = date.getTime();
  return itinerary.hotels.filter((h) => {
    const range = parseNightRange(h.nights, year);
    if (!range) return false;
    const start = range.start.getTime();
    const end = range.end.getTime();
    if (start === end) return t === start; // single-date stay
    return t >= start && t < end; // multi-night: checkout day excluded
  });
}

/** Enclosing bounding box of a set of points, or `null` if empty. */
export function boundsForPoints(points: LatLng[]): Bounds | null {
  if (points.length === 0) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const p of points) {
    if (p.lng < west) west = p.lng;
    if (p.lng > east) east = p.lng;
    if (p.lat < south) south = p.lat;
    if (p.lat > north) north = p.lat;
  }
  return { west, south, east, north };
}

/**
 * Bounding box for everything shown at a selection (stops + relevant hotels).
 * Used to `fitBounds` when the user picks a day or "All days".
 */
export function boundsForSelection(
  itinerary: Itinerary,
  selection: DaySelection,
  year: number,
): Bounds | null {
  const points: LatLng[] = markerPointsForSelection(itinerary, selection, year);
  return boundsForPoints(points);
}

/** A renderable marker: a `dot` for a stop, or a `pin` for a hotel. */
export interface MarkerPoint extends LatLng {
  name: string;
  category: Category;
  kind: 'dot' | 'pin';
  /** Time (stops) or night range (hotels). */
  meta?: string;
  note?: string;
  url?: string;
  placeId?: string;
}

function locationKey(p: LatLng): string {
  return `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`;
}

/**
 * Compose the markers to render for a selection.
 *
 * Non-hotel stops become `dot` markers. Hotels become distinct `pin` markers,
 * built from the relevant hotels (carrying their night range) and merged with
 * any hotel-category stops, deduplicated by location so a lodging never renders
 * twice. Kept pure so it can be unit-tested independently of MapLibre.
 */
export function markerPointsForSelection(
  itinerary: Itinerary,
  selection: DaySelection,
  year: number,
): MarkerPoint[] {
  const stops = stopsForSelection(itinerary, selection);

  const dots: MarkerPoint[] = stops
    .filter((s) => s.category !== 'hotel')
    .map((s) => ({
      name: s.name,
      category: s.category,
      lat: s.lat,
      lng: s.lng,
      kind: 'dot',
      meta: s.time,
      note: s.note,
      url: s.url,
      placeId: s.placeId,
    }));

  const pins = new Map<string, MarkerPoint>();
  for (const h of hotelsForSelection(itinerary, selection, year)) {
    pins.set(locationKey(h), {
      name: h.name,
      category: 'hotel',
      lat: h.lat,
      lng: h.lng,
      kind: 'pin',
      meta: h.nights,
      note: h.note,
      placeId: h.placeId,
    });
  }
  // Fold in hotel-category stops not already represented (defensive).
  for (const s of stops.filter((s) => s.category === 'hotel')) {
    const key = locationKey(s);
    if (!pins.has(key)) {
      pins.set(key, {
        name: s.name,
        category: 'hotel',
        lat: s.lat,
        lng: s.lng,
        kind: 'pin',
        meta: s.time,
        note: s.note,
        url: s.url,
        placeId: s.placeId,
      });
    }
  }

  return [...dots, ...pins.values()];
}
